/**
 * Schedule data cache using localStorage for persistence across sessions.
 * Caches production schedule data per-week with resource group filter support.
 */

import type { SchedulingBoardEntry } from '../services/types';

const CACHE_PREFIX = 'scheduleWeek_';
const DEFAULT_TTL = 15 * 60 * 1000; // 15 minutes

interface WeekCacheEntry {
  data: SchedulingBoardEntry[];
  timestamp: number;
}

/**
 * Format a Date to YYYY-MM-DD string
 */
export function formatDateKey(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Get the Monday of the week for a given date
 */
export function getWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the Sunday (end) of the week for a given Monday
 */
export function getWeekSunday(monday: Date): Date {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return sunday;
}

/**
 * Build cache key for a week with optional resource group
 */
function buildCacheKey(weekMonday: string, resourceGrpID?: string): string {
  const grpSuffix = resourceGrpID ? `_${resourceGrpID}` : '_ALL';
  return CACHE_PREFIX + weekMonday + grpSuffix;
}

/**
 * Get cached data for a specific week
 */
export function getCachedWeek(
  weekMonday: string,
  resourceGrpID?: string,
  ttl: number = DEFAULT_TTL
): SchedulingBoardEntry[] | null {
  try {
    const cacheKey = buildCacheKey(weekMonday, resourceGrpID);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const entry: WeekCacheEntry = JSON.parse(cached);
    const age = Date.now() - entry.timestamp;

    if (age < ttl) {
      return entry.data;
    }

    localStorage.removeItem(cacheKey);
    return null;
  } catch {
    return null;
  }
}

/**
 * Cache data for a specific week
 */
export function setCachedWeek(
  weekMonday: string,
  resourceGrpID: string | undefined,
  data: SchedulingBoardEntry[]
): void {
  try {
    const cacheKey = buildCacheKey(weekMonday, resourceGrpID);
    const entry: WeekCacheEntry = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear all schedule cache entries
 */
export function clearScheduleCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}
